import {
  Typography,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ProductCard from "./ProductCard/ProductCard";
import { Product } from "../types/Product";

interface ProductShowcaseProps {
  products: Product[];
  title?: string;
  layout?: 'left-title' | 'right-title';
  showDiscount?: boolean;
  onProductClick?: (productSku: string) => void;
}

export default function ProductShowcase({ 
  products, 
  title = "Productos destacados",
  layout = 'left-title',
  showDiscount = true,
  onProductClick
}: ProductShowcaseProps) {
  const isLeftTitle = layout === 'left-title';
  
   const navigate = useNavigate();
  
  const handleProductClick = (productSku: string) => {
    if (onProductClick) {
      onProductClick(productSku);
    } else {
      navigate(`/productos/${productSku}`);
    }
  };

  const TitleBox = () => (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100%', 
        backgroundColor: 'grey.200',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 0,
        minWidth: {
          xs: '100%',
          md: '250px',
        },
        '@media (max-width: 768px)': {
          height: '190px',
        },
      }}
    >
      <Typography 
        variant="h3" 
        sx={{ 
          fontWeight: 'bold',
          color: 'black',
          textAlign: 'center',
          pl: {
            xs: 2,
            md: 3,
          },
          pr: {
            xs: 2,
            md: 0,
          },
          fontFamily: '"Rubik", sans-serif',
          fontSize: {
            xs: '2.5rem',
            sm: '2.5rem',
            md: '2.5rem',
            lg: '3rem',
          },
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  const GridSection = () => (
    <Box sx={{ 
      width: {
        xs: '100%',
        md: '50%',
      },
      height: {
        xs: 'auto',
        md: '100%',
      },
      display: 'flex', 
      flexDirection: {
        xs: 'column',
        md: 'column',
      },
      '@media (max-width: 768px)': {
        gap: 0,
      },
    }}>
      <Box sx={{ 
        display: {
          xs: 'none',
          md: 'flex',
        },
        height: '50%' 
      }}>
        {isLeftTitle ? (
          <>
            <Box sx={{ width: '50%', height: '100%', minWidth: '250px' }}>
              <TitleBox />
            </Box>
            <Box sx={{ width: '50%', height: '100%', minWidth: '250px' }}>
              <ProductCard 
                product={products[0]} 
                variant="showcase-small"
                onClick={() => handleProductClick(products[0].sku)}
              />
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ width: '50%', height: '100%', minWidth: '250px' }}>
              <ProductCard 
                product={products[0]} 
                variant="showcase-small"
                onClick={() => handleProductClick(products[0].sku)}
              />
            </Box>
            <Box sx={{ width: '50%', height: '100%', minWidth: '250px' }}>
              <ProductCard 
                product={products[1]} 
                variant="showcase-small"
                onClick={() => handleProductClick(products[1].sku)}
              />
            </Box>
          </>
        )}
      </Box>
      
      <Box sx={{ 
        display: {
          xs: 'none',
          md: 'flex',
        },
        height: '50%' 
      }}>
        <Box sx={{ width: '50%', height: '100%', minWidth: '250px' }}>
          <ProductCard 
            product={isLeftTitle ? products[1] : products[2]} 
            variant="showcase-small"
            onClick={() => isLeftTitle ? handleProductClick(products[1].sku) : handleProductClick(products[2].sku) }
          />
        </Box>
        <Box sx={{ width: '50%', height: '100%', minWidth: '250px' }}>
          <ProductCard 
            product={isLeftTitle ? products[2] : products[3]} 
            variant="showcase-small"
            onClick={() => isLeftTitle ? handleProductClick(products[2].sku) : handleProductClick(products[3].sku) }
          />
        </Box>
      </Box>

      <Box sx={{
        display: {
          xs: 'flex', 
          md: 'none',
        },
        flexDirection: 'column',
        gap: 0,
      }}>
        {isLeftTitle && (
          <Box sx={{ 
            width: '100%',
            height: '190px', 
          }}>
            <TitleBox />
          </Box>
        )}
        
        <Box sx={{ 
          width: '100%',
          aspectRatio: '3/4',
        }}>
          <ProductCard 
            product={products[0]} 
            variant="showcase-small"
            onClick={() => handleProductClick(products[0].sku)}
          />
        </Box>
        
        <Box sx={{ 
          width: '100%',
          aspectRatio: '3/4',
        }}>
          <ProductCard 
            product={products[1]} 
            variant="showcase-small"
            onClick={() => handleProductClick(products[1].sku)}
          />
        </Box>
        
        <Box sx={{ 
          width: '100%',
          aspectRatio: '3/4',
        }}>
          <ProductCard 
            product={products[2]} 
            variant="showcase-small"
            onClick={() => handleProductClick(products[2].sku)}
          />
        </Box>
      </Box>
    </Box>
  );

  const LargeProductSection = () => (
    <Box sx={{ 
      width: {
        xs: '100%',
        md: '50%',
      },
      height: {
        xs: 'auto',
        md: '100%',
      },
      minWidth: {
        xs: '100%',
        md: '500px'
      },
      '@media (max-width: 768px)': {
        aspectRatio: '3/4',
      },
    }}>
      <ProductCard 
        product={products[3]} 
        variant="showcase-large"
        onClick={() => handleProductClick(products[3].sku)}
      />
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      height: {
        xs: 'auto',
        md: 950,
      },
      '@media (max-width: 768px)': {
        flexDirection: 'column',
        gap: 0,
      },
    }}>
      {/* Desktop layout */}
      <Box sx={{
        display: {
          xs: 'none',
          md: 'flex',
        },
        width: '100%',
        height: '100%',
      }}>
        {isLeftTitle ? (
          <>
            <GridSection />
            <LargeProductSection />
          </>
        ) : (
          <>
            <LargeProductSection />
            <GridSection />
          </>
        )}
      </Box>

      {/* Mobile layout - Always show in order: Title (if exists), then products */}
      <Box sx={{
        display: {
          xs: 'flex',
          md: 'none',
        },
        flexDirection: 'column',
        gap: 0,
      }}>
        {/* Show title first for left-title layout */}
        {isLeftTitle && title && (
          <Box sx={{ 
            width: '100%',
            height: '190px', 
          }}>
            <TitleBox />
          </Box>
        )}
        
        {/* Large product card */}
        <Box sx={{ 
          width: '100%',
          aspectRatio: '3/4',
        }}>
          <ProductCard 
            product={products[3]} 
            variant="showcase-large"
            onClick={() => handleProductClick(products[3].sku)}
          />
        </Box>
        
        {/* Small product cards */}
        <Box sx={{ 
          width: '100%',
          aspectRatio: '3/4',
        }}>
          <ProductCard 
            product={products[0]} 
            variant="showcase-small"
            onClick={() => handleProductClick(products[0].sku)}
          />
        </Box>
        
        <Box sx={{ 
          width: '100%',
          aspectRatio: '3/4',
        }}>
          <ProductCard 
            product={products[1]} 
            variant="showcase-small"
            onClick={() => handleProductClick(products[1].sku)}
          />
        </Box>
        
        <Box sx={{ 
          width: '100%',
          aspectRatio: '3/4',
        }}>
          <ProductCard 
            product={products[2]} 
            variant="showcase-small"
            onClick={() => handleProductClick(products[2].sku)}
          />
        </Box>
      </Box>
    </Box>
  );
}














